import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import {
  TraceabilityRequests,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface CreateProductionSiteVariables {
  request: TraceabilityRequests.CreateProductionSite
}

export const useCreateProductionSiteMutation = (
  options?: UseMutationOptions<
    TraceabilityResponses.ProductionSite,
    AxiosError,
    CreateProductionSiteVariables
  >
) => {
  const { t } = useTranslation("suppliers")

  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      const res = (
        await httpClient.post<TraceabilityResponses.ProductionSite>(
          `/production-sites`,
          variables.request
        )
      ).data

      return res
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      showSuccessNotification({
        title: t("productionSites.create.success.title"),
        message: t("productionSites.create.success.message"),
      })
      await queryClient.invalidateQueries({
        queryKey: ["listProductionSites"],
      })
    },
    onError: async (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      showAxiosError({
        notificationTitle: t("productionSites.create.error"),
        notificationMessage: error.message,
        showNotification: true,
        error,
      })
    },
  })
}
